<template>
    <!-- <MobileView v-if="isMobile && !continueOnMobile" /> -->

    <!-- <router-view v-else-if="!isLoading" /> -->
    <router-view v-if="!isLoading" />

    <notifications :position="isRtl ? 'top left' : 'top right'">
        <template #body="props">
            <AppAlertBody :item="props.item" :close="props.close" />
        </template>
    </notifications>
    <CookieModal v-if="showCookieBanner" />

    <!-- Walkthrough tours -->
    <WalkthroughGeneric v-if="isNonNullable(walkthroughId)" />

    <!-- Idle Warning Modal -->
    <IdleWarningModal
        v-if="isWarning"
        :is-warning="isWarning"
        :warning-type="warningType"
        :time-until-timeout="timeUntilTimeout"
        :extend-session="extendSession" />

    <div v-if="isLoading" class="loading-overlay bg-light">
        <LoadingIndicator />
    </div>
</template>

<script setup lang="ts">
import { useAuthStore } from '@/stores/auth';
import { isRtl } from './i18n';
import { useLanguageResolution } from '@/composables/useLanguageResolution';
import { useWindowSize, useSessionStorage } from '@vueuse/core';
import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { provideAlert } from '@/composables/useAlert';
import { isNonNullable } from './utils/nullability-helpers';
import { useEventsStore } from '@/stores/events.store';
import { provideWalkthrough } from '@/composables/useWalkthrough';
import { useIdleTimer } from '@/composables/useIdleTimer';
import { useCookieConsent } from '@/composables/useCookieConsent';
import { usePwaInstall } from '@/composables/usePwaInstall';
import { syncManifestStartUrl } from '@/utils/pwa-manifest-start-url';
import { CookieModal, WalkthroughGeneric, IdleWarningModal } from '@/async-components';

const { isFetchingUser, currentOrganization } = useAuthStore();

const hasFetchedOrganization = computed(() => isNonNullable(currentOrganization.value));

// Runtime UI-language resolution (user preference -> current org default -> platform default).
useLanguageResolution();

const { width, height } = useWindowSize();

const route = useRoute();

provideAlert();

useEventsStore();

const { walkthroughId } = provideWalkthrough();

const { showBanner: showCookieBanner } = useCookieConsent();

// Captures the beforeinstallprompt event as early as possible so it's available
// by the time the user reaches the Courses page, where the install banner is shown.
usePwaInstall();

// Keeps the manifest's start_url in sync with the current route, so an installed
// home-screen icon opens back to whichever page the user installed it from.
watch(
    () => route.name,
    (name) => syncManifestStartUrl(typeof name === 'string' ? name : undefined),
    { immediate: true },
);

// Initialize idle timer with organization-specific settings
const { isWarning, warningType, timeUntilTimeout, extendSession } = useIdleTimer({
    timeoutMs: 30 * 60 * 1000, // 30 minutes
    warningMs: 2 * 60 * 1000, // 2 minutes warning
    refreshSessionMs: 5 * 60 * 1000, // 5 minutes before timeout
    enableSessionRefresh: true,
    enableWarning: true,
});

const isLoading = computed(
    () => isFetchingUser.value || (route.meta.requireOrganizationSelection && !hasFetchedOrganization.value),
);

// The widest viewport I could find is the iPhone 15 Pro Max with 430px.
// const isMobile = computed(() => width.value < 435 || height.value < 435);
// const continueOnMobile = useSessionStorage('continueOnMobile', false);
</script>

<style scoped lang="scss">
.loading-overlay {
    position: fixed !important;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1054;
    opacity: 0.85;
}
</style>
